import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { apiUrl } from '../config';
import { STRIPE_PUBLISHABLE_KEY } from '../config';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function CheckoutForm({ plan, subscriptionId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Use confirmPayment without redirect for embedded checkout
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required', // Only redirect if 3D Secure is needed
      });

      if (submitError) {
        setError(submitError.message);
        setProcessing(false);
      } else {
        // Immediately sync subscription status in backend so expired -> active
        if (subscriptionId) {
          const token = localStorage.getItem('accessToken');
          try {
            await fetch(apiUrl('/api/billing/sync-subscription'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ subscriptionId }),
            });
          } catch (syncError) {
            console.error('Failed to sync subscription after payment:', syncError);
          }
        }

        // Payment succeeded - call onSuccess callback
        onSuccess();
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <PaymentElement />

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.buttonGroup}>
        <button
          type="submit"
          disabled={!stripe || processing}
          style={{
            ...styles.submitButton,
            ...((!stripe || processing) && styles.submitButtonDisabled)
          }}
        >
          {processing ? 'Processing...' : `Subscribe to ${plan === 'basic' ? 'Basic' : 'Pro'} Plan`}
        </button>

        <button
          type="button"
          onClick={onCancel}
          style={styles.cancelButton}
          disabled={processing}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function EmbeddedCheckout({ plan, onSuccess, onCancel }) {
  const [clientSecret, setClientSecret] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const fetchPaymentIntent = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(apiUrl('/api/billing/create-subscription'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ plan }),
        });

        const data = await response.json();

        if (data.success && data.clientSecret) {
          setClientSecret(data.clientSecret);
          setSubscriptionId(data.subscriptionId || '');
        } else {
          setError(data.error || 'Failed to initialize checkout');
        }
      } catch (err) {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentIntent();
  }, [plan]);

  const options = {
    clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#14b8a6',
        colorBackground: 'rgba(255, 255, 255, 0.06)',
        colorText: '#ffffff',
        colorDanger: '#ef4444',
        fontFamily: 'Space Grotesk, Inter, -apple-system, sans-serif',
        borderRadius: '8px',
        spacingUnit: '4px',
      },
      rules: {
        '.Input': {
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          color: '#ffffff',
        },
        '.Input:focus': {
          border: '2px solid #14b8a6',
          boxShadow: '0 0 0 2px rgba(20, 184, 166, 0.2)',
        },
        '.Label': {
          color: '#ffffff',
          fontWeight: '600',
        },
      },
    },
  };

  const overlayStyle = {
    ...styles.container,
    ...(isVisible ? styles.containerVisible : styles.containerHidden),
  };

  const cardStyle = {
    ...styles.card,
    ...(isVisible ? styles.cardVisible : styles.cardHidden),
  };

  let bodyContent = (
    <>
      <h2 style={styles.title}>Complete Your Subscription</h2>
      <p style={styles.subtitle}>
        {plan === 'basic' ? 'Basic Plan - $20/month' : 'Pro Plan - $50/month'}
      </p>

      {clientSecret && (
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm
            plan={plan}
            subscriptionId={subscriptionId}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        </Elements>
      )}
    </>
  );

  if (loading) {
    bodyContent = (
      <>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading checkout...</p>
      </>
    );
  }

  if (error) {
    bodyContent = (
      <>
        <div style={styles.errorIcon}>✕</div>
        <h2 style={styles.title}>Checkout Error</h2>
        <p style={styles.text}>{error}</p>
        <button onClick={onCancel} style={styles.submitButton}>
          Go Back
        </button>
      </>
    );
  }

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        {bodyContent}
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: `
      rgba(7, 12, 22, 0.65),
      radial-gradient(circle at 18% 22%, rgba(34, 211, 238, 0.16), transparent 30%),
      radial-gradient(circle at 80% 8%, rgba(99, 102, 241, 0.2), transparent 32%),
      linear-gradient(135deg, #0b1224, #0c1e38)
    `,
    backdropFilter: 'blur(12px)',
    transition: 'opacity 260ms ease, backdrop-filter 260ms ease',
  },
  containerHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  containerVisible: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  card: {
    background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.12), rgba(99, 102, 241, 0.16))',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto',
    transition: 'opacity 260ms ease, transform 320ms cubic-bezier(0.19, 1, 0.22, 1)',
  },
  cardHidden: {
    opacity: 0,
    transform: 'translateY(60px) scale(0.96)',
  },
  cardVisible: {
    opacity: 1,
    transform: 'translateY(0) scale(1)',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '12px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '16px',
    color: '#e2e8f0',
    marginBottom: '30px',
    textAlign: 'center',
  },
  text: {
    fontSize: '16px',
    color: '#e2e8f0',
    marginBottom: '20px',
    lineHeight: '1.6',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  error: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '2px solid #ef4444',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '16px',
    color: '#fca5a5',
    fontSize: '14px',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '24px',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #0f9b8e 0%, #14b8a6 50%, #a7f3d0 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 30px rgba(20, 184, 166, 0.35)',
  },
  submitButtonDisabled: {
    background: 'linear-gradient(135deg, #6b8886 0%, #7a9d98 50%, #c7ddd6 100%)',
    cursor: 'not-allowed',
    boxShadow: '0 5px 15px rgba(20, 184, 166, 0.15)',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#14b8a6',
    border: '2px solid rgba(20, 184, 166, 0.5)',
    borderRadius: '14px',
    padding: '14px 32px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  spinner: {
    width: '60px',
    height: '60px',
    margin: '0 auto 20px',
    border: '4px solid rgba(255, 255, 255, 0.1)',
    borderTop: '4px solid #14b8a6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#e2e8f0',
    textAlign: 'center',
    fontSize: '16px',
  },
  errorIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto 20px',
    backgroundColor: '#ef4444',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: 'white',
    fontWeight: 'bold',
  },
};
